"use client";

/**
 * Keep an in-progress payment method across a remount.
 *
 * Checkout survives more interruptions than an ordinary form. A UPI handoff
 * backgrounds the web view and can come back to a reloaded document; a native
 * app can be evicted under memory pressure; people leave the screen to go and
 * look up a VPA. Every one of those can bring the payment page back with
 * `useState` at its initial value — so somebody who already typed their UPI id
 * finds an empty field and has to type it again. Session recordings show them
 * abandoning instead.
 *
 * ## What is stored, and what deliberately is not
 *
 * Stored: the UPI id, its "save as default" choice, the cardholder name and the
 * card nickname.
 *
 * NOT stored: the card number, the expiry, or the CVV. That is a rule, not
 * caution. The CVV must never be retained after authorisation by anyone, and a
 * PAN written to web storage is plaintext cardholder data sitting where any
 * injected script can read it — it would turn one XSS into a card breach.
 * Remembering a card is what the "Save this card" checkbox is for: Razorpay
 * tokenises it and holds it, and we never see it again.
 *
 * ## Why `sessionStorage`
 *
 * It restores the draft across a reload and the return from a UPI app, and it is
 * gone once the app session ends — so a shared device never prefills the next
 * person's checkout with somebody else's VPA, which `localStorage` would.
 * `clearPrivateBrowserState()` already clears it on an account transition, so no
 * sign-out wiring is needed here.
 */

const DRAFT_STORAGE_KEY = "hiive:payment-method-draft:v1";

/** Long enough for any real VPA or name; a guard against a junk write. */
const MAX_FIELD_LENGTH = 128;

export type PaymentMethodDraft = {
  upiId: string;
  saveUpi: boolean;
  cardName: string;
  cardNickname: string;
};

const readField = (value: unknown): string =>
  typeof value === "string" ? value.slice(0, MAX_FIELD_LENGTH) : "";

/**
 * Read the stored draft, or `null` when there is nothing usable.
 *
 * Never throws: storage access is blocked outright in some privacy modes, and a
 * checkout must not fail to render because of it.
 */
export function readPaymentMethodDraft(): PaymentMethodDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    const draft: PaymentMethodDraft = {
      upiId: readField(record.upiId),
      saveUpi: record.saveUpi === true,
      cardName: readField(record.cardName),
      cardNickname: readField(record.cardNickname),
    };
    const hasAnything =
      draft.upiId.length > 0 ||
      draft.cardName.length > 0 ||
      draft.cardNickname.length > 0;
    return hasAnything ? draft : null;
  } catch {
    return null;
  }
}

/** Persist the draft, or drop the entry once every field is empty. */
export function writePaymentMethodDraft(draft: PaymentMethodDraft): void {
  if (typeof window === "undefined") return;
  const hasAnything =
    draft.upiId.trim().length > 0 ||
    draft.cardName.trim().length > 0 ||
    draft.cardNickname.trim().length > 0;
  try {
    if (!hasAnything) {
      window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      DRAFT_STORAGE_KEY,
      JSON.stringify({
        upiId: draft.upiId.slice(0, MAX_FIELD_LENGTH),
        saveUpi: draft.saveUpi,
        cardName: draft.cardName.slice(0, MAX_FIELD_LENGTH),
        cardNickname: draft.cardNickname.slice(0, MAX_FIELD_LENGTH),
      }),
    );
  } catch {
    // Storage can be full or blocked. Losing the draft is acceptable here,
    // not a new failure, so there is nothing to report.
  }
}

export function clearPaymentMethodDraft(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // See `writePaymentMethodDraft`.
  }
}
